import React from 'react';
import ReactDOM from 'react-dom';

import { createRoot, type Root } from 'react-dom/client';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { type IntlValues, Notice } from '../../components/Notice';
import { type NoticeType } from '../../components/Notice/noticeTypes';

const reactRoots = new WeakMap<Element, Root>();

/** Mounts `element` with React 18/19 when `nike_r19_render_unmount_2` is on, else uses legacy ReactDOM.render. */
const renderToMountPoint = (element: React.ReactElement, mountPoint: Element) => {
	if (fg('nike_r19_render_unmount_2')) {
		let root = reactRoots.get(mountPoint);

		if (!root) {
			root = createRoot(mountPoint);
			reactRoots.set(mountPoint, root);
		}

		root.render(element);
	} else {
		ReactDOM.render(element, mountPoint);
	}
};

/** Unmounts with React 18/19 when `nike_r19_render_unmount_2` is on, else uses legacy ReactDOM.unmountComponentAtNode. */
const unmountFromMountPoint = (mountPoint: Element): boolean => {
	if (fg('nike_r19_render_unmount_2')) {
		const root = reactRoots.get(mountPoint);
		if (root) {
			root.unmount();
			reactRoots.delete(mountPoint);
			return true;
		}
		return false;
	}

	return ReactDOM.unmountComponentAtNode(mountPoint);
};

let locale = 'en-US';

// Sets a locale variable that controls the locale used for the notice component.
// Since the component is rendered by ReactDOM.render, there is no parent IntlProvider.
// This means we can't use `useIntl` in the Notice and instead need to pass in the locale.
//
// Ideally, in the future we refactor to remove ReactDOM.render and just rely on a store/controller
// to determine if the notice is visible or not.
export function setNoticeLocale(newLocale: string): void {
	locale = newLocale;
}

/**
 * @deprecated Use the new `useShowNotice` hook from @atlassian/proforma-utils instead
 */
export function showNotice(
	noticeType: NoticeType,
	intlValues?: IntlValues,
	errorDetails?: string,
): Promise<boolean> {
	return new Promise((resolve) => {
		const dialogTargetNode = document.getElementById('pf-dialog-container');
		if (!dialogTargetNode) {
			// eslint-disable-next-line no-console
			console.error(
				`Could not show notice ${noticeType}. The target Node for the dialog is not available`,
			);
			resolve(false);
			return;
		}

		const handleClose = (confirmed?: boolean): void => {
			resolve(!!confirmed);
			closeNotice();
		};

		renderToMountPoint(
			<Notice
				noticeType={noticeType}
				handleClose={handleClose}
				settings={{ intlValues, errorDetails, customLocale: locale }}
			/>,
			dialogTargetNode,
		);
	});
}

function closeNotice(): boolean {
	const dialogTargetNode = document.getElementById('pf-dialog-container');
	if (!dialogTargetNode) {
		// eslint-disable-next-line no-console
		console.error(`Could not close notice. The target Node for the dialog is not available`);
		return false;
	}

	const closed = unmountFromMountPoint(dialogTargetNode);
	if (!closed) {
		// eslint-disable-next-line no-console
		console.error(`Could not close notice. There is no dialog to unmount`);
	}

	return closed;
}
